import { useRef } from 'react';
import {
    InputLabel,
    FormControl,
    Select,
    MenuItem,
    FormControlProps,
    ListSubheader,
    Divider,
} from '@mui/material';
import { FieldError, Path, Control, Controller } from 'react-hook-form';
import { FormErrorText } from '@/components/forms/other/ErrorText/FormErrorText';
import { usePhrase } from '@/hooks/general/usePhrase/usePhrase';
import { GENERAL_PHRASES } from '@/consts/phrases/generalPhrases';
import { isEmpty } from '@/utils/fp';
import { LabelWithTooltip } from '@/components/forms/other/LabelWithTooltip/LabelWithTooltip';
import { InfoTooltip } from '@/components/general/InfoTooltip/InfoTooltip';

export type AllowedValueTypes = string | number;

export type SelectFieldData<TSelectValue extends AllowedValueTypes = string | number> = {
    label: string | number;
    useRawLabel?: boolean;
    value: TSelectValue;
    infoTooltip?: string | number;
};

export type SelectGroupData<TSelectValue extends AllowedValueTypes = string | number> = {
    groupId: number;
    label: string | number;
    data: SelectFieldData<TSelectValue>[] | undefined;
};

interface CustomSelectFieldProps<
    T extends object,
    TSelectValue extends AllowedValueTypes = string | number,
> {
    control: Control<T>;
    name: Path<T>;
    label: string | number;
    data: SelectFieldData<TSelectValue>[] | SelectGroupData<TSelectValue>[] | undefined;
    onChange?: (val: any) => void;
    multiple?: boolean;
    value?: TSelectValue | TSelectValue[];
    error?: FieldError | null;
    hasAllOption?: boolean;
}

type SelectFieldProps<
    T extends object,
    TSelectValue extends AllowedValueTypes = string | number,
> = CustomSelectFieldProps<T, TSelectValue> & Omit<FormControlProps, 'error' | 'label' | 'name'>;

export const SelectField = <
    T extends object,
    TSelectValue extends AllowedValueTypes = string | number,
>(
    props: SelectFieldProps<T, TSelectValue>,
) => {
    const { t } = usePhrase();
    const { label, data, name, control, error, multiple, onChange, hasAllOption, ...rest } = props;
    const formControlRef = useRef<HTMLDivElement>(null);
    const labelRef = useRef<HTMLLabelElement>(null);

    const createSubHeader = (item: SelectGroupData<TSelectValue>) => {
        return (
            <ListSubheader key={item.groupId}>
                {typeof item.label === 'string' ? item.label : t(item.label)}
            </ListSubheader>
        );
    };

    const itemWithLabelAndStringValue = (
        item: SelectFieldData<TSelectValue> | SelectGroupData<TSelectValue>,
    ): item is SelectFieldData<TSelectValue> => 'label' in item && 'value' in item;

    const createMenuItem = (
        item: SelectFieldData<TSelectValue>,
    ): string | React.ReactElement | React.ReactElement[] | number => {
        const value = item.value;
        const selectMenuItemId = `select-menu-item-${value}`;

        return (
            <MenuItem
                sx={{ flex: 'row', justifyContent: 'space-between' }}
                className="selectOption"
                key={selectMenuItemId}
                value={value}
            >
                {item.useRawLabel || typeof item.label === 'string' ? item.label : t(item.label)}
                {item.infoTooltip && <InfoTooltip title={item.infoTooltip} />}
            </MenuItem>
        );
    };

    const createMultipleMenuItems = (items: SelectFieldData<TSelectValue>[] | undefined) => {
        if (!items || isEmpty(items)) return;
        return items.filter(itemWithLabelAndStringValue).map(createMenuItem);
    };

    const createMenuItemsWithOptionalSubheader = (
        data: SelectFieldData<TSelectValue>[] | SelectGroupData<TSelectValue>[] | undefined,
    ) => {
        if (!data || isEmpty(data)) return;
        return data.map((dataItem, idx) => {
            if (!dataItem || isEmpty(dataItem)) return;
            if ('groupId' in dataItem) {
                return [createSubHeader(dataItem), createMultipleMenuItems(dataItem.data)];
            } else if (dataItem.value === 'divider') {
                return <Divider key={`divider-${idx}`} />;
            } else if (itemWithLabelAndStringValue(dataItem)) {
                return createMenuItem(dataItem);
            }
        });
    };

    // disable label animation on load
    formControlRef.current?.classList.add('label-noanim');

    /**
     * This function is required to only return the label of the selected option,
     * and not include tooltips or other elements that are only required in the menu.
     */
    const getSelectedLabel = (selected?: TSelectValue | TSelectValue[]) => {
        if (!data || selected === null || selected === undefined) return '';
        const selectedArray = Array.isArray(selected) ? selected : [selected];

        const selectedLabels = selectedArray
            .map((value) => {
                const selectedOption = data
                    .flatMap((item) =>
                        'data' in item ? item.data?.find((o) => o.value === value) : item,
                    )
                    .find((o) => o?.value === value);

                if (selectedOption?.label) {
                    return typeof selectedOption.label === 'string'
                        ? selectedOption.label
                        : t(selectedOption.label);
                }
                return '';
            })
            .filter((label) => label !== '');

        return selectedLabels.join(', ');
    };

    return (
        <FormControl {...rest} ref={formControlRef}>
            <Controller
                name={name}
                control={control}
                render={({
                    field: { value, onChange: defaultOnChange, onBlur, ref },
                    fieldState: { error: fieldError },
                }) => {
                    const isError = Boolean(error || fieldError);
                    return (
                        <>
                            <InputLabel id={`${name}-label`} error={isError} ref={labelRef}>
                                <LabelWithTooltip label={label} labelRef={labelRef} />
                            </InputLabel>
                            <Select
                                labelId={`${name}-label`}
                                name={name}
                                multiple={multiple}
                                onBlur={onBlur}
                                value={value ?? (multiple ? [] : '')}
                                error={isError}
                                onChange={(event) => {
                                    const value = Array.isArray(event.target.value)
                                        ? event.target.value.join(',')
                                        : event.target.value;
                                    defaultOnChange(event);
                                    onChange?.(value);
                                }}
                                onFocus={() => {
                                    const element = formControlRef.current;
                                    if (element?.classList.contains('label-noanim')) {
                                        element?.classList.remove('label-noanim');
                                    }
                                }}
                                inputProps={{
                                    inputRef: ref,
                                    'aria-labelledby': `${name}-label`,
                                }}
                                renderValue={(selected) => getSelectedLabel(selected)}
                            >
                                {hasAllOption && (
                                    <MenuItem className="selectOption" key={'All'} value={'All'}>
                                        {t(GENERAL_PHRASES.ALL)}
                                    </MenuItem>
                                )}
                                {createMenuItemsWithOptionalSubheader(data)}
                            </Select>
                            <FormErrorText error={error || fieldError} />
                        </>
                    );
                }}
            />
        </FormControl>
    );
};
